import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  Handle,
  Node,
  NodeProps,
  Position,
  addEdge,
  useEdgesState,
  useNodesState,
  useUpdateNodeInternals,
} from 'reactflow';
// @ts-ignore
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import dagre from 'dagre';
import { FiGitBranch, FiInbox, FiLayers, FiPieChart } from 'react-icons/fi';
import { GoTag } from 'react-icons/go';
import { MdAccessTime, MdClose, MdOutlineMail } from 'react-icons/md';
import { RiDraggable } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
// @ts-ignore
import 'reactflow/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../../app/store';
import DetailPanel from '../../components/DetailPanel';

const RiDraggableComponent = RiDraggable as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const MdCloseComponent = MdClose as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const GoTagComponent = GoTag as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const MdEmailComponent = MdOutlineMail as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const MdAccessTimeComponent = MdAccessTime as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiInboxComponent = FiInbox as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiGitBranchComponent = FiGitBranch as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiLayersComponent = FiLayers as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiPieChartComponent = FiPieChart as unknown as React.ComponentType<{ size?: number | string; className?: string }>;

interface Condition { category: string; operator: string; value: string; }
interface Bucket { name: string; weight: number; to?: string; }

export interface MultiSplitRuleCondition {
  entity: string;
  field: string;
  operator: string;
  value: any;
}

export interface MultiSplitRule {
  id: string;
  condition: string | MultiSplitRuleCondition;
  to: string;
  label: string;
}

interface NodeData {
  label: string; id: string; type?: string; tag?: string | string[]; selectedTags?: string[];
  template?: string; variantId?: string; variables?: { name: string; address: string }; trigger?: string;
  onNodeClick?: (node: Node<NodeData>) => void; onDeleteConnection?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void; delayType?: 'fixed' | 'specific'; amount?: number;
  unit?: 'days' | 'weeks' | 'months' | 'hours' | 'minutes'; date?: string; time?: string;
  conditions?: Condition[]; maxWaitAmount?: number; maxWaitUnit?: string; buckets?: Bucket[];
  isExit?: boolean; isBranch?: boolean; branchConditions?: any[]; conditionLogic?: 'AND' | 'OR';
  isMultiSplit?: boolean; multiSplitRules?: MultiSplitRule[]; defaultTo?: string; onUpdateNode?: (node: Node<NodeData>) => void;
}

interface LayoutedElements { nodes: Node<NodeData>[]; edges: Edge[]; }

interface HierarchyGraphEditProps {
  initialNodes?: Array<{
    branchConditions: never[]; isBranch: boolean; id: string; label: string; type?: string;
    position: { x: number; y: number }; tag?: string | string[]; selectedTags?: string[];
    template?: string; variantId?: string; variables?: { name: string; address: string };
    delayType?: string; amount?: number; unit?: string; date?: string; time?: string;
    maxWaitAmount?: number; maxWaitUnit?: string; conditions?: any[];
    isMultiSplit?: boolean; multiSplitRules?: MultiSplitRule[]; defaultTo?: string; buckets?: Bucket[];
    conditionLogic?: 'AND' | 'OR';
  }>;
  initialEdges?: Array<{ id: string; source: string; target: string; sourceHandle?: string }>;
  automation?: {
    trigger?: string;
    graph?: { nodes?: Record<string, { id: string; type: string; cfg?: any; edges?: { next?: string; true?: string; false?: string;[key: string]: any } }> };
  };
}

export const syncMultiSplitData = (nodes: Node<NodeData>[], edges: Edge[]): Node<NodeData>[] => {
  return nodes.map((node) => {
    if (!node.data.isMultiSplit && node.type !== 'multiSplit' && node.data.type !== 'multi') {
      return node;
    }
    const rules = node.data.multiSplitRules || [];
    if (rules.length === 0) return node;

    const defaultRule = rules.find((r) => r.label === 'Default') || rules[0];
    const defaultEdge = edges.find((e) => e.source === node.id && e.sourceHandle === `rule-${defaultRule.id}`);
    const defaultTo = defaultEdge ? defaultEdge.target : (node.data.defaultTo || '');

    const updatedRules = rules.map((r) => {
      const edge = edges.find((e) => e.source === node.id && e.sourceHandle === `rule-${r.id}`);
      return {
        ...r,
        to: edge ? edge.target : (r.to || ''),
      };
    });

    return {
      ...node,
      data: {
        ...node.data,
        defaultTo,
        multiSplitRules: updatedRules,
      },
    };
  });
};

const defaultNodeWidth = 200;
const defaultNodeHeight = 60;
const homeNodeWidth = 300;
const homeNodeHeight = 200;
const branchNodeWidth = 220;
const branchNodeHeight = 100;
const multiSplitNodeWidth = 280;
const multiSplitNodeHeight = 180;

export const getLayoutedElements = (
  nodes: Node<NodeData>[],
  edges: Edge[]
): LayoutedElements => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 50, nodesep: 50 });

  const nodeMap = new Map<string, Node<NodeData>>();
  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
    let width = defaultNodeWidth, height = defaultNodeHeight;
    if (node.id === 'home') { width = homeNodeWidth; height = homeNodeHeight; }
    else if (node.data.isExit) { height = 40; width = 120; }
    else if (node.data.isBranch) { width = branchNodeWidth; height = branchNodeHeight; }
    else if (node.data.isMultiSplit) { width = multiSplitNodeWidth; height = multiSplitNodeHeight + (node.data.multiSplitRules?.length || 3) * 20; }
    else if (node.data.type === 'wait' && (node.data.conditions?.length ?? 0) > 0) { height += 30; }
    else if (node.data.type === 'percent') { height += 20; }
    dagreGraph.setNode(node.id, { width, height });
  });

  const getHandleOrder = (sourceNode: Node<NodeData> | undefined, handleId?: string | null): number => {
    if (!handleId || !sourceNode) return 0;
    if (handleId === 'true') return 0;
    if (handleId === 'false') return 1;
    if (handleId.startsWith('percent-')) { const idx = parseInt(handleId.replace('percent-', ''), 10); return isNaN(idx) ? 0 : idx; }
    if (handleId.startsWith('rule-')) { const ruleId = handleId.replace('rule-', ''); const idx = sourceNode.data.multiSplitRules?.findIndex((r) => r.id === ruleId); return idx !== undefined && idx !== -1 ? idx : 0; }
    return 0;
  };

  const sortedEdges = [...edges].sort((a, b) => {
    if (a.source === b.source) { const sourceNode = nodeMap.get(a.source); return getHandleOrder(sourceNode, a.sourceHandle) - getHandleOrder(sourceNode, b.sourceHandle); }
    return 0;
  });

  const outEdgesMap = new Map<string, Edge[]>();
  sortedEdges.forEach((edge) => { if (!outEdgesMap.has(edge.source)) outEdgesMap.set(edge.source, []); outEdgesMap.get(edge.source)!.push(edge); });

  const visited = new Set<string>();
  const queue: string[] = ['home'];
  const orderedEdges: Edge[] = [];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    const outEdges = outEdgesMap.get(currentId) || [];
    outEdges.forEach((edge) => { orderedEdges.push(edge); if (!visited.has(edge.target)) queue.push(edge.target); });
  }
  sortedEdges.forEach((edge) => { if (!orderedEdges.some((e) => e.id === edge.id)) orderedEdges.push(edge); });
  orderedEdges.forEach((edge) => { dagreGraph.setEdge(edge.source, edge.target); });

  dagre.layout(dagreGraph);

  const processedBranchSources = new Set<string>();

  const getSubtreeNodeIds = (rootId: string): string[] => {
    const result: string[] = [];
    const q = [rootId];
    const seen = new Set<string>();
    while (q.length > 0) {
      const curr = q.shift()!;
      if (seen.has(curr)) continue;
      seen.add(curr);
      result.push(curr);
      orderedEdges.forEach((e) => { if (e.source === curr && !seen.has(e.target)) q.push(e.target); });
    }
    return result;
  };

  orderedEdges.forEach((edge) => {
    if (!edge.sourceHandle) return;
    if (processedBranchSources.has(edge.source)) return;
    const sourceNode = nodeMap.get(edge.source);
    const branchEdges = orderedEdges
      .filter((e) => e.source === edge.source && e.sourceHandle)
      .sort((a, b) => getHandleOrder(sourceNode, a.sourceHandle) - getHandleOrder(sourceNode, b.sourceHandle));
    if (branchEdges.length < 2) return;
    processedBranchSources.add(edge.source);

    const subtrees = branchEdges.map((e) => ({
      nodeId: e.target,
      handleOrder: getHandleOrder(sourceNode, e.sourceHandle),
      rootX: dagreGraph.node(e.target)?.x ?? 0,
      nodeIds: getSubtreeNodeIds(e.target),
    }));

    const sortedXPositions = [...subtrees].sort((a, b) => a.rootX - b.rootX).map((s) => s.rootX);
    const desiredOrder = [...subtrees].sort((a, b) => a.handleOrder - b.handleOrder);
    if (desiredOrder.every((s, i) => Math.abs(s.rootX - sortedXPositions[i]) < 1)) return;

    desiredOrder.forEach((subtree, i) => {
      const delta = sortedXPositions[i] - subtree.rootX;
      if (Math.abs(delta) < 1) return;
      subtree.nodeIds.forEach((nodeId) => {
        const pos = dagreGraph.node(nodeId);
        if (pos) pos.x += delta;
      });
    });
  });

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (nodeWithPosition) {
      node.position = { x: nodeWithPosition.x - nodeWithPosition.width / 2, y: nodeWithPosition.y - nodeWithPosition.height / 2 };
    }
  });

  return { nodes, edges: orderedEdges };
};

const customCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    const specificTarget = pointerCollisions.find((c) => c.id !== 'reactflow-canvas');
    if (specificTarget) return [specificTarget];
    return pointerCollisions;
  }
  return rectIntersection(args);
};

const DroppablePercentBranch: React.FC<{ nodeId: string; bucket: Bucket; index: number }> = ({ nodeId, bucket, index }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `droppable-branch-${nodeId}-${index}`, data: { type: 'branch-target', nodeId, branchIndex: index, branchName: bucket.name } });
  const handleColor = index === 0 ? '#3B82F6' : '#10B981';
  return (
    <div ref={setNodeRef} className={`relative flex items-center flex-1 py-1 px-1 rounded transition-all duration-200 ${isOver ? 'scale-110 shadow-md ring-2 ring-offset-1 z-20' : ''}`}
      style={{ backgroundColor: isOver ? (index === 0 ? '#EFF6FF' : '#ECFDF5') : 'transparent', ...(isOver ? { ringColor: handleColor } : {}) }}>
      <Handle type="source" position={Position.Bottom} id={`percent-${index}`}
        style={{ background: 'transparent', width: '12px', height: '12px', position: 'relative', marginBottom: '2px', border: '2px solid white', borderRadius: '50%', transform: isOver ? 'scale(1.3)' : 'scale(1)', transition: 'all 0.2s ease', visibility: 'hidden' }} />
      <span className={`text-[10px] font-semibold transition-all duration-200 ${isOver ? 'font-bold' : ''}`} style={{ color: handleColor, textShadow: isOver ? `0 0 6px ${handleColor}88` : 'none' }}>
        {bucket.name} ({bucket.weight}%)
      </span>
      {isOver && <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap z-30 pointer-events-none animate-bounce" style={{ backgroundColor: handleColor }}>Drop on {bucket.name}</div>}
    </div>
  );
};

const DroppableMultiBranch: React.FC<{ nodeId: string; rule: MultiSplitRule; index: number; totalRules: number }> = ({ nodeId, rule, index, totalRules }) => {
  const droppableId = `droppable-multi-branch-${nodeId}-${rule.id}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { type: 'multi-branch-target', nodeId, ruleId: rule.id, branchIndex: index, ruleLabel: rule.label },
  });
  const handleColor = index === 0 ? '#9CA3AF' : '#3B82F6';

  // Dynamically adjust text size based on total rules count to save space
  const textSize = totalRules > 6 ? 'text-[8px]' : totalRules > 4 ? 'text-[9px]' : 'text-[10px]';

  return (
    <div
      ref={setNodeRef}
      className={`relative flex flex-col items-center flex-1 min-w-0 py-1 px-0.5 rounded transition-all duration-200 ${isOver ? 'scale-110 shadow-md ring-2 ring-offset-1 z-20' : ''
        }`}
      style={{
        backgroundColor: isOver ? (index === 0 ? '#F3F4F6' : '#EFF6FF') : 'transparent',
        ...(isOver ? { ringColor: handleColor } : {}),
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        id={`rule-${rule.id}`}
        style={{
          position: 'relative',
          transform: isOver ? 'scale(1.3)' : 'none',
          left: 'auto',
          bottom: 'auto',
          top: 'auto',
          right: 'auto',
          background: handleColor,
          width: '8px',
          height: '8px',
          marginBottom: '2px',
          border: '2px solid white',
          borderRadius: '50%',
          transition: 'all 0.2s ease',
        }}
      />
      <span
        className={`${textSize} font-medium truncate w-full text-center transition-all duration-200 ${isOver ? 'font-bold' : ''
          }`}
        style={{ color: handleColor, textShadow: isOver ? `0 0 6px ${handleColor}88` : 'none' }}
      >
        {rule.label}
      </span>
      {isOver && (
        <div
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap z-30 pointer-events-none animate-bounce"
          style={{ backgroundColor: handleColor }}
        >
          Drop on {rule.label}
        </div>
      )}
    </div>
  );
};

// --- Droppable Branch Handle for True/False Branch ---
const DroppableTrueFalseBranch: React.FC<{ nodeId: string; handleType: 'true' | 'false'; label: string }> = ({ nodeId, handleType, label }) => {
  const droppableId = `droppable-truefalse-${nodeId}-${handleType}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { type: 'truefalse-branch-target', nodeId, handleType, label },
  });
  const handleColor = handleType === 'true' ? '#10B981' : '#EF4444';
  const bgColor = handleType === 'true' ? '#ECFDF5' : '#FEF2F2';
  const glowShadow = handleType === 'true'
    ? '0 0 15px 3px rgba(16, 185, 129, 0.6)'
    : '0 0 15px 3px rgba(239, 68, 68, 0.6)';

  return (
    <div
      ref={setNodeRef}
      className={`relative flex flex-col items-center flex-1 py-2 px-3 rounded transition-all duration-200 ${isOver ? 'scale-110 shadow-lg ring-2 ring-offset-1 z-20' : ''}`}
      style={{
        backgroundColor: isOver ? bgColor : 'transparent',
        ...(isOver ? { ringColor: handleColor, boxShadow: glowShadow } : {}),
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        id={handleType}
        style={{
          position: 'relative',
          transform: isOver ? 'scale(1.4)' : 'none',
          left: 'auto',
          bottom: 'auto',
          top: 'auto',
          right: 'auto',
          background: handleColor,
          width: '14px',
          height: '14px',
          marginBottom: '4px',
          border: '2px solid white',
          borderRadius: '50%',
          transition: 'all 0.2s ease',
          boxShadow: isOver ? `0 0 12px ${handleColor}` : 'none',
        }}
      />
      <span
        className={`text-[11px] font-semibold transition-all duration-200 ${isOver ? 'font-bold' : ''}`}
        style={{
          color: handleColor,
          textShadow: isOver ? `0 0 8px ${handleColor}66` : 'none'
        }}
      >
        {label}
      </span>
      {isOver && (
        <div
          className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white px-2 py-0.5 rounded shadow-lg whitespace-nowrap z-30 pointer-events-none animate-bounce"
          style={{ backgroundColor: handleColor }}
        >
          Drop on {label}
        </div>
      )}
    </div>
  );
};

const DroppableExitNode: React.FC<{ id: string; data: NodeData; selected?: boolean; menuRef?: React.RefObject<HTMLDivElement | null> }> = ({ id, data, selected }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `droppable-exit-${id}`, data: { type: 'exit-target', exitNodeId: id, label: data.label } });
  const isExitA = data.label?.includes('A');
  const isExitB = data.label?.includes('B');
  const branchColor = isExitA ? '#3B82F6' : isExitB ? '#10B981' : '#00A4BD';
  const glowShadow = isExitA ? '0 0 15px 3px rgba(59,130,246,0.6)' : isExitB ? '0 0 15px 3px rgba(16,185,129,0.6)' : '0 0 15px 3px rgba(0,164,189,0.6)';
  return (
    <div ref={setNodeRef} className={`relative p-2 bg-gray-100 rounded shadow-sm w-28 text-xs text-center transition-all duration-200 ${selected ? 'ring-2 ring-[#00A4BD]' : ''} ${isOver ? 'scale-110 shadow-lg ring-2 ring-offset-1 z-20' : ''}`}
      style={{ border: isOver ? `2px solid ${branchColor}` : '1px solid #d1d5db', backgroundColor: isOver ? (isExitA ? '#EFF6FF' : isExitB ? '#ECFDF5' : '#F0FDFA') : '#F3F4F6', boxShadow: isOver ? glowShadow : undefined }}>
      <Handle type="target" position={Position.Top} id="top" style={{ top: '-5px', background: isOver ? branchColor : '#000', width: '10px', height: '10px', boxShadow: isOver ? `0 0 8px ${branchColor}` : undefined, transition: 'all 0.2s ease' }} />
      <span className={`font-semibold transition-all duration-200 ${isOver ? 'font-bold' : 'text-gray-600'}`} style={isOver ? { color: branchColor } : {}}>{data.label}</span>
      {isOver && <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap z-30 pointer-events-none animate-bounce" style={{ backgroundColor: branchColor }}>Drop on {isExitA ? 'A' : isExitB ? 'B' : 'Exit'}</div>}
    </div>
  );
};

const CustomHomeNode: React.FC<NodeProps<NodeData>> = ({ data, id, selected }) => {
  const trigger = useSelector((state: RootState) => state.automation.trigger);
  const conditions = useSelector((state: RootState) => state.automation.conditions || []);
  const conditionCount = conditions?.length || 0;
  const triggerLabels: Record<string, string> = { form_submission: 'Form Submission', new_subscriber: 'New Subscriber', email_opened: 'Email Opened', link_clicked: 'Link Clicked', scheduled_date_time: 'Scheduled Date/Time', FormSubmission: 'Form Submission', NewSubscriber: 'New Subscriber' };
  return (
    <div className={`relative p-3 rounded bg-white shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'}`} style={{ width: '260px' }} onMouseDown={(e) => { if (id === 'home') e.stopPropagation(); }}>
      <Handle type="source" position={Position.Bottom} id="home-bottom" style={{ bottom: '-5px', background: '#000', width: '10px', height: '10px' }} />
      <div className="flex items-start gap-2 mb-1">
        <FiInboxComponent className="text-gray-600 text-lg mt-[5px]" />
        <div>
          <span className="font-semibold text-gray-800 text-sm">Trigger</span>
          <div className="text-gray-600 text-xs">{trigger ? triggerLabels[trigger] || trigger : 'No Trigger Selected'}</div>
        </div>
      </div>
      {conditionCount > 0 && <div className="absolute -bottom-[1.3rem] left-0 bg-[#00A4BD] text-white text-[10px] font-medium rounded px-2 py-0.5 shadow-md whitespace-nowrap z-10">{conditionCount} Condition{conditionCount > 1 ? 's' : ''}</div>}
    </div>
  );
};

const BranchNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={menuRef} className={`relative bg-white rounded-lg shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'}`} style={{ width: '280px' }}
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (data.onNodeClick) data.onNodeClick({ id: data.id, data } as Node<NodeData>); }}>
      <Handle type="target" position={Position.Top} id="top" style={{ top: '-5px', background: '#000', width: '10px', height: '10px' }} />
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center"><FiGitBranchComponent className="text-sm" /></div>
          <span className="font-semibold text-gray-800 text-sm">True/False Branch</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }} className="text-gray-400 hover:text-gray-600">⋮</button>
        </div>
      </div>
      <div className="flex justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <DroppableTrueFalseBranch nodeId={data.id} handleType="true" label="True" />
        <DroppableTrueFalseBranch nodeId={data.id} handleType="false" label="False" />
      </div>
      {menuOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-sm text-xs z-10" onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ top: '100%', right: 0 }}>
          <div className="px-3 py-1 hover:bg-gray-100 cursor-pointer" onClick={() => { if (data.onDeleteNode) data.onDeleteNode(data.id); setMenuOpen(false); }}>Delete</div>
        </div>
      )}
    </div>
  );
};

const MultiSplitNode: React.FC<NodeProps<NodeData>> = ({ id, data, selected }) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const rules = data.multiSplitRules || [{ id: uuidv4(), condition: '', to: '', label: 'Default' }];

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, data.multiSplitRules, updateNodeInternals]);

  return (
    <div
      ref={menuRef}
      className={`relative bg-white rounded-lg shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'
        }`}
      style={{ width: '320px' }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onNodeClick) {
          data.onNodeClick({ id, data: { ...data, multiSplitRules: rules } } as Node<NodeData>);
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ top: '-5px', background: '#000', width: '10px', height: '10px' }}
      />
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center">
            <FiLayersComponent className="text-sm" />
          </div>
          <span className="font-semibold text-gray-800 text-sm">Multi Split</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">{rules.length} rules</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            ⋮
          </button>
        </div>
      </div>
      <div className="flex justify-between items-center px-2 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg overflow-hidden">
        {rules.map((rule, index) => (
          <DroppableMultiBranch
            key={rule.id}
            nodeId={id}
            rule={rule}
            index={index}
            totalRules={rules.length}
          />
        ))}
      </div>
      {menuOpen && (
        <div
          className="absolute right-0 top-full mt-1 bg-white border rounded shadow-sm text-xs z-10"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          style={{ top: '100%', right: 0 }}
        >
          <div
            className="px-3 py-1 hover:bg-gray-100 cursor-pointer"
            onClick={() => {
              if (data.onDeleteNode) data.onDeleteNode(data.id);
              setMenuOpen(false);
            }}
          >
            Delete
          </div>
        </div>
      )}
    </div>
  );
};

// --- Branch Detail Panel ---
const BranchDetailPanel: React.FC<{ node: Node<NodeData>; onUpdateNode: (node: Node<NodeData>) => void }> = ({ node, onUpdateNode }) => {
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [showConditionSelect, setShowConditionSelect] = useState(false);
  const [conditions, setConditions] = useState<any[]>(node.data.branchConditions || []);
  const [conditionLogic, setConditionLogic] = useState<'AND' | 'OR'>('AND');

  const conditionOptions = [
    {
      category: 'Email',
      options: [
        { label: 'Email Subscription', value: 'Email:email_subscription' },
        { label: 'Email Engagement', value: 'Email:email_engagement' },
        { label: 'Email Status', value: 'Email:email_status' },
        { label: 'Email Interaction', value: 'Email:email_interaction' },
      ],
    },
    {
      category: 'Contact',
      options: [
        { label: 'First Name', value: 'Contact:first_name' },
        { label: 'Last Name', value: 'Contact:last_name' },
        { label: 'Email', value: 'Contact:email' },
      ],
    },
  ];

  useEffect(() => {
    if (node.data.branchConditions) {
      setConditions(node.data.branchConditions);
    }
    if (node.data.conditionLogic) {
      setConditionLogic(node.data.conditionLogic);
    }
  }, [node.data.branchConditions, node.data.conditionLogic]);

  const isConditionAlreadyUsed = useCallback((value: string) => {
    return conditions.some(cond => {
      const condValue = `${cond.entity}:${cond.field}`;
      return condValue === value;
    });
  }, [conditions]);

  const addCondition = useCallback(() => {
    if (!selectedValue) return;

    if (isConditionAlreadyUsed(selectedValue)) {
      toast.warning('This condition has already been added. Please remove the existing one before adding it again.', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    const [entity, field] = selectedValue.split(':');
    const newCondition = {
      entity,
      field,
      operator: 'EXISTS',
      value: null,
      label: `Condition ${conditions.length + 1}`,
    };
    const newConditions = [...conditions, newCondition];
    setConditions(newConditions);
    setSelectedValue('');
    setShowConditionSelect(false);
    onUpdateNode({
      ...node,
      data: {
        ...node.data,
        branchConditions: newConditions,
        conditionLogic: conditionLogic
      }
    });
  }, [selectedValue, conditions, node, onUpdateNode, conditionLogic, isConditionAlreadyUsed]);

  const removeCondition = useCallback((index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
    onUpdateNode({
      ...node,
      data: {
        ...node.data,
        branchConditions: newConditions,
        conditionLogic: conditionLogic
      }
    });
  }, [conditions, node, onUpdateNode, conditionLogic]);

  const toggleConditionLogic = useCallback(() => {
    const newLogic = conditionLogic === 'AND' ? 'OR' : 'AND';
    setConditionLogic(newLogic);
    onUpdateNode({
      ...node,
      data: {
        ...node.data,
        branchConditions: conditions,
        conditionLogic: newLogic
      }
    });
  }, [conditionLogic, conditions, node, onUpdateNode]);

  const formatConditionDisplay = (cond: any) => {
    if (!cond) return '';
    const allOpts = conditionOptions.flatMap((c) => c.options);
    const optMatch = allOpts.find((o) => o.value === `${cond.entity}:${cond.field}`);
    const fieldText = optMatch ? optMatch.label : cond.field;
    return `${cond.entity || ''} → ${fieldText}`;
  };

  return (
    <div className="bg-white rounded-lg">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">True/False Branch</h3>

      <div className="space-y-2 mb-4">
        {conditions.map((cond, index) => (
          <div key={index} className="bg-gray-50 border border-gray-200 rounded p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Condition {index + 1}</span>
              <button onClick={() => removeCondition(index)} className="text-red-500 hover:text-red-700">
                <MdCloseComponent size={16} />
              </button>
            </div>
            <div className="text-xs text-blue-600 mt-1 font-medium">{formatConditionDisplay(cond)}</div>
            {index < conditions.length - 1 && (
              <div className="text-xs font-semibold text-gray-500 mt-2">
                {conditionLogic}
              </div>
            )}
          </div>
        ))}
      </div>

      {conditions.length > 0 && (
        <div className="mb-3 flex items-center justify-start">
          <div className="relative inline-flex items-center rounded-lg bg-gray-100 p-1 shadow-inner border border-gray-200">
            <button
              onClick={() => {
                if (conditionLogic !== 'AND') toggleConditionLogic();
              }}
              className={`
                px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200
                ${conditionLogic === 'AND'
                  ? 'bg-white text-gray-800 shadow-md border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700 bg-transparent border border-transparent'}
              `}
            >
              And
            </button>
            <button
              onClick={() => {
                if (conditionLogic !== 'OR') toggleConditionLogic();
              }}
              className={`
                px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200
                ${conditionLogic === 'OR'
                  ? 'bg-[#E0F7FA] text-[#00838F] shadow-md'
                  : 'text-gray-500 hover:text-gray-700 bg-transparent'}
              `}
            >
              Or
            </button>
          </div>
        </div>
      )}

      {!showConditionSelect ? (
        <button
          onClick={() => setShowConditionSelect(true)}
          className="w-full border border-dashed border-[#00A4BD] rounded p-2 text-[#00A4BD] text-sm hover:bg-blue-50"
        >
          + Add Condition
        </button>
      ) : (
        <div className="border border-blue-200 rounded p-3 bg-blue-50">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Select Condition</label>
          <select
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#00A4BD]"
          >
            <option value="">-- Choose a condition --</option>
            {conditionOptions.map((group, idx) => (
              <optgroup key={idx} label={group.category}>
                {group.options.map((option) => {
                  const isUsed = isConditionAlreadyUsed(option.value);
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={isUsed}
                      className={isUsed ? 'text-gray-400 bg-gray-50' : ''}
                    >
                      {option.label} {isUsed ? '(Already Added)' : ''}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
          <div className="flex gap-2 mt-3">
            <button
              onClick={addCondition}
              disabled={!selectedValue || isConditionAlreadyUsed(selectedValue)}
              className={`px-4 py-1.5 rounded text-sm ${selectedValue && !isConditionAlreadyUsed(selectedValue) ? 'bg-[#00A4BD] text-white hover:bg-[#008BA0]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowConditionSelect(false);
                setSelectedValue('');
              }}
              className="px-4 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Multi Split Detail Panel with Select Dropdown ---
const MultiSplitDetailPanel: React.FC<{ node: Node<NodeData>; onUpdateNode: (node: Node<NodeData>) => void }> = ({ node, onUpdateNode }) => {
  const [rules, setRules] = useState<MultiSplitRule[]>(node.data.multiSplitRules || [{ id: uuidv4(), condition: '', to: '', label: 'Default' }]);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [showConditionSelect, setShowConditionSelect] = useState(false);

  const conditionOptions = [
    {
      category: 'Email',
      options: [
        { label: 'Email Subscription', value: 'Email:email_subscription' },
        { label: 'Email Engagement', value: 'Email:email_engagement' },
        { label: 'Email Status', value: 'Email:email_status' },
        { label: 'Email Interaction', value: 'Email:email_interaction' },
      ],
    },
    {
      category: 'Contact',
      options: [
        { label: 'First Name', value: 'Contact:first_name' },
        { label: 'Last Name', value: 'Contact:last_name' },
        { label: 'Email', value: 'Contact:email' },
      ],
    },
  ];

  useEffect(() => {
    if (node.data.multiSplitRules) {
      setRules(node.data.multiSplitRules);
    }
  }, [node.data.multiSplitRules]);

  const isConditionAlreadyUsed = useCallback((value: string) => {
    return rules.some(rule => {
      if (typeof rule.condition === 'string') return false;
      if (!rule.condition) return false;
      const condValue = `${rule.condition.entity}:${rule.condition.field}`;
      return condValue === value;
    });
  }, [rules]);

  const updateRules = useCallback((newRules: MultiSplitRule[]) => {
    // Determine if we need short labels (C1, C2) or long labels (Condition 1, Condition 2)
    const useShortLabels = newRules.length > 4;

    const relabeledRules = newRules.map((r, idx) => ({
      ...r,
      label: idx === 0
        ? 'Default'
        : (useShortLabels ? `C${idx}` : `Condition ${idx}`),
    }));

    setRules(relabeledRules);
    onUpdateNode({ ...node, data: { ...node.data, multiSplitRules: relabeledRules } });
  }, [node, onUpdateNode]);

  const addCondition = useCallback(() => {
    if (!selectedValue) return;

    if (isConditionAlreadyUsed(selectedValue)) {
      toast.warning('This condition has already been added. Please remove the existing one before adding it again.', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    const [entity, field] = selectedValue.split(':');
    const newRule: MultiSplitRule = {
      id: uuidv4(),
      condition: {
        entity,
        field,
        operator: 'EXISTS',
        value: null,
      },
      to: '',
      label: `Condition ${rules.length}`,
    };
    updateRules([...rules, newRule]);
    setSelectedValue('');
    setShowConditionSelect(false);
  }, [selectedValue, rules, updateRules, isConditionAlreadyUsed]);

  const removeRule = useCallback((ruleId: string) => {
    if (rules.length <= 1) return;
    updateRules(rules.filter((r) => r.id !== ruleId));
  }, [rules, updateRules]);

  const formatConditionDisplay = (cond: string | MultiSplitRuleCondition) => {
    if (!cond) return '';
    if (typeof cond === 'string') return cond;
    const allOpts = conditionOptions.flatMap((c) => c.options);
    const optMatch = allOpts.find((o) => o.value === `${cond.entity}:${cond.field}` || o.value.endsWith(`:${cond.field}`));
    const fieldText = optMatch ? optMatch.label : cond.field;
    return `${cond.entity || ''} → ${fieldText}`;
  };

  return (
    <div className="bg-white rounded-lg">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Multi Split</h3>
      <div className="space-y-2 mb-4">
        {rules.map((rule, index) => (
          <div key={rule.id} className={`bg-gray-50 border border-gray-200 rounded p-3 ${index === 0 ? 'hidden' : ''}`}>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">{rule.label || `Condition ${index}`}</span>
              {index > 0 && (
                <button onClick={() => removeRule(rule.id)} className="text-red-500 hover:text-red-700">
                  <MdCloseComponent size={16} />
                </button>
              )}
            </div>
            <div className="text-xs text-blue-600 mt-1 font-medium">{formatConditionDisplay(rule.condition)}</div>
          </div>
        ))}
      </div>
      {!showConditionSelect ? (
        <button
          onClick={() => setShowConditionSelect(true)}
          className="w-full border border-dashed border-[#00A4BD] rounded p-2 text-[#00A4BD] text-sm hover:bg-blue-50"
        >
          + Add Condition
        </button>
      ) : (
        <div className="border border-blue-200 rounded p-3 bg-blue-50">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Select Condition</label>
          <select
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#00A4BD]"
          >
            <option value="">-- Choose a condition --</option>
            {conditionOptions.map((group, idx) => (
              <optgroup key={idx} label={group.category}>
                {group.options.map((option) => {
                  const isUsed = isConditionAlreadyUsed(option.value);
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={isUsed}
                      className={isUsed ? 'text-gray-400 bg-gray-50' : ''}
                    >
                      {option.label} {isUsed ? '(Already Added)' : ''}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
          <div className="flex gap-2 mt-3">
            <button
              onClick={addCondition}
              disabled={!selectedValue || isConditionAlreadyUsed(selectedValue)}
              className={`px-4 py-1.5 rounded text-sm ${selectedValue && !isConditionAlreadyUsed(selectedValue) ? 'bg-[#00A4BD] text-white hover:bg-[#008BA0]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowConditionSelect(false);
                setSelectedValue('');
              }}
              className="px-4 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PercentageSplitDetailPanel: React.FC<{ node: Node<NodeData>; onUpdateNode: (node: Node<NodeData>) => void }> = ({ node, onUpdateNode }) => {
  const [buckets, setBuckets] = useState<Bucket[]>(node.data.buckets || [{ name: 'A', weight: 50 }, { name: 'B', weight: 50 }]);
  useEffect(() => { if (node.data.buckets) setBuckets(node.data.buckets); }, [node.data.buckets]);
  const percentA = buckets[0]?.weight || 50;
  const percentB = 100 - percentA;
  const colorA = '#3B82F6', colorB = '#10B981';
  const updateSplit = useCallback((newPercentA: number) => {
    const clamped = Math.max(0, Math.min(100, newPercentA));
    const newBuckets = [{ ...buckets[0], weight: clamped }, { ...buckets[1], weight: 100 - clamped }];
    setBuckets(newBuckets);
    onUpdateNode({ ...node, data: { ...node.data, buckets: newBuckets } });
  }, [buckets, node, onUpdateNode]);
  return (
    <div className="p-6 bg-white rounded-lg">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Percentage Split</h3>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Condition</p>
      <div className="mb-4">
        <input type="range" min="0" max="100" value={percentA} onChange={(e) => updateSplit(parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, ${colorA} 0%, ${colorA} ${percentA}%, ${colorB} ${percentA}%, ${colorB} 100%)` }} />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: colorA }} /><span className="text-sm font-medium text-gray-700">A</span><span className="text-sm font-semibold text-gray-900">{percentA}%</span></div>
        <div className="flex items-center gap-2"><span className="text-sm font-semibold text-gray-900">{percentB}%</span><span className="text-sm font-medium text-gray-700">B</span><span className="w-3 h-3 rounded-full" style={{ backgroundColor: colorB }} /></div>
      </div>
    </div>
  );
};

const CustomNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  let IconComponent = null, subText = null;
  if (data.type === 'tags') { IconComponent = GoTagComponent; subText = data.selectedTags && data.selectedTags.length > 0 ? data.selectedTags.join(', ') : data.tag && typeof data.tag === 'string' ? data.tag : Array.isArray(data.tag) ? data.tag.join(', ') : 'No Tags'; }
  else if (data.type === 'send') { IconComponent = MdEmailComponent; const tl: Record<string, string> = { registration_confirmation: 'Registration Confirmation Template', welcome: 'Welcome Email Template', newsletter: 'Newsletter Template' }; subText = data.template ? tl[data.template] : 'Select Template'; }
  else if (data.type === 'time') { IconComponent = MdAccessTimeComponent; if (data.delayType === 'fixed' && data.amount && data.unit) subText = `${data.amount} ${data.amount === 1 ? data.unit.slice(0, -1) : data.unit.charAt(0).toUpperCase() + data.unit.slice(1)}`; else if (data.delayType === 'specific' && data.date && data.time) subText = `${data.date} ${data.time}`; else subText = 'Set time delay'; }
  else if (data.type === 'wait') { IconComponent = MdAccessTimeComponent; subText = data.maxWaitAmount && data.maxWaitUnit ? `${data.maxWaitAmount} ${data.maxWaitAmount === 1 ? data.maxWaitUnit.slice(0, -1) : data.maxWaitUnit.charAt(0).toUpperCase() + data.maxWaitUnit.slice(1)}` : 'Set Condition'; }
  else if (data.type === 'percent') { IconComponent = FiPieChartComponent; const tw = data.buckets?.reduce((sum, b) => sum + (b.weight || 0), 0) || 0; subText = `${data.buckets?.length || 0} branches (${tw}%)`; }
  else if (data.type === 'multi') { IconComponent = FiLayersComponent; const rc = data.multiSplitRules?.length || 1; subText = `${rc} ${rc === 1 ? 'rule' : 'rules'}`; }
  else if (data.isExit) { return <DroppableExitNode id={data.id} data={data} selected={selected} menuRef={menuRef} />; }

  return (
    <div ref={menuRef} className={`relative p-3 bg-white rounded shadow-md w-44 text-xs cursor-pointer ${selected ? 'ring-2 ring-[#00A4BD]' : ''}`}
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (data.onNodeClick) data.onNodeClick({ id: data.id, data } as Node<NodeData>); }}>
      <Handle type="target" position={Position.Top} id="top" style={{ top: '-5px', background: '#000', width: '10px', height: '10px' }} />
      {data.type === 'multi' && data.multiSplitRules && data.multiSplitRules.length > 0 && (
        <div className="flex justify-between px-2 py-2 mt-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {data.multiSplitRules.map((rule, index) => (
            <DroppableMultiBranch key={rule.id} nodeId={data.id} rule={rule} index={index} totalRules={data.multiSplitRules?.length || 1} />
          ))}
        </div>
      )}
      {data.type === 'percent' && data.buckets && data.buckets.length > 0 && (
        <div className="flex justify-between gap-1 px-1 py-1.5 mt-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {data.buckets.map((bucket, index) => (
            <DroppablePercentBranch key={index} nodeId={data.id} bucket={bucket} index={index} />
          ))}
        </div>
      )}
      {!data.isMultiSplit && !data.isBranch && data.type !== 'percent' && data.type !== 'multi' && (
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ bottom: '-5px', background: '#000', width: '10px', height: '10px' }} />
      )}
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-2">
          {IconComponent && <IconComponent className="text-gray-600 text-lg" />}
          <div>
            <div className="font-semibold">{data.label}</div>
            {subText && <div className="text-gray-600 text-xs mt-1">{data.type === 'percent' ? null : subText}</div>}
          </div>
        </div>
        <div className="relative">
          <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); setMenuOpen((o) => !o); }} className="text-gray-500 hover:text-gray-800">⋮</button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-sm text-xs z-10" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <div className="px-3 py-1 hover:bg-gray-100 cursor-pointer" onClick={() => { if (data.onDeleteNode) data.onDeleteNode(data.id); setMenuOpen(false); }}>Delete</div>
            </div>
          )}
        </div>
      </div>
      {data.type === 'wait' && Array.isArray(data.conditions) && data.conditions.length > 0 && (
        <div className="text-[#FFFFFF] border border-[#00A4BD] bg-[#00A4BD] text-xs rounded rounded-tl-none px-1 py-0.5 absolute top-[55px] left-[0.1px]">
          {data.conditions.length} Condition{data.conditions.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { customHome: CustomHomeNode, custom: CustomNode, branch: BranchNode, multiSplit: MultiSplitNode };

const SidebarItem: React.FC<{ type: string; label: string }> = ({ type, label }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `sidebar-item-${type}-${label}`, data: { type, label } });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={`flex relative items-center p-2 mb-2 bg-[#F5F8FA] rounded cursor-move text-[14px] text-[#3F5265] ${isDragging ? 'opacity-50 border-blue-500' : ''}`}>
      <div className="mr-2"><RiDraggableComponent /></div>{label}
    </div>
  );
};

const DroppableCanvas: React.FC<{ children: React.ReactNode; id: string }> = ({ children, id }) => {
  const { setNodeRef } = useDroppable({ id });
  return <div ref={setNodeRef} className="flex-1 h-[650px]">{children}</div>;
};

function normalizeFromApi(
  initialNodes: HierarchyGraphEditProps['initialNodes'] = [],
  initialEdges: HierarchyGraphEditProps['initialEdges'] = [],
  automation?: HierarchyGraphEditProps['automation']
) {
  const apiNodes = automation?.graph?.nodes;
  const edgeStyle = { stroke: '#D000E5', strokeWidth: 2, filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))' };

  if (apiNodes && Object.keys(apiNodes).length > 0) {
    const flowNodes: Node<NodeData>[] = [];
    const flowEdges: Edge[] = [];
    const triggerType = automation?.trigger || '';
    flowNodes.push({ id: 'home', type: 'customHome', data: { label: 'Trigger', id: 'home', trigger: triggerType }, position: { x: 0, y: 0 } });

    Object.entries(apiNodes).forEach(([, n]: [string, any]) => {
      if (n.type === 'TRIGGER' || n.id === 'nTRIGGER') return;
      let type = 'custom', label = n.type || 'Step';
      let extra: Partial<NodeData> = {};

      if (n.type === 'TAG_ADD' || n.type === 'TAG') {
        type = 'tags';
        label = 'Tags';
        extra = { selectedTags: n.cfg?.tags || [], tag: n.cfg?.tags || [] };
      }
      else if (n.type === 'EXIT') {
        type = 'exit';
        label = 'Exit';
        extra = { isExit: true };
      }
      else if (n.type === 'SEND_EMAIL' || n.type === 'EMAIL' || n.type === 'send') {
        type = 'send';
        label = 'Send Email';
        extra = { template: n.cfg?.campaignId || '', variantId: n.cfg?.variantId || '' };
      }
      else if (n.type === 'WAIT_UNTIL' || n.type === 'WAIT' || n.type === 'wait') {
        type = 'wait';
        label = 'Wait Until';
        let delayAmount = 1, delayUnit = 'days';
        if (n.cfg?.delay) {
          if (typeof n.cfg.delay === 'object') {
            if (n.cfg.delay.days > 0) { delayAmount = n.cfg.delay.days; delayUnit = 'days'; }
            else if (n.cfg.delay.hours > 0) { delayAmount = n.cfg.delay.hours; delayUnit = 'hours'; }
            else if (n.cfg.delay.minutes > 0) { delayAmount = n.cfg.delay.minutes; delayUnit = 'minutes'; }
          } else if (typeof n.cfg.delay === 'string') {
            const parts = n.cfg.delay.trim().split(/\s+/);
            if (parts.length >= 2) { delayAmount = parseInt(parts[0], 10) || 1; delayUnit = parts[1].toLowerCase().replace(/s$/, '') + 's'; }
          }
        }
        extra = { maxWaitAmount: delayAmount, maxWaitUnit: delayUnit, amount: delayAmount, unit: delayUnit as any, conditions: n.cfg?.conditions || [] };
      }
      else if (n.type === 'MULTI_SPLIT' || n.type === 'multi') {
        type = 'multi';
        label = 'Multi Split';
        const rules = n.cfg?.rules || [];
        const defaultTo = n.cfg?.defaultTo || '';

        const uiRules: MultiSplitRule[] = [
          {
            id: 'rule-default',
            condition: '',
            to: defaultTo,
            label: 'Default',
          },
          ...rules.map((rule: any, index: number) => ({
            id: rule.id || `rule-${index + 1}`,
            condition: rule.condition || { entity: 'Email', field: '', operator: 'EXISTS', value: null },
            to: rule.to || '',
            label: rule.label || `Condition ${index + 1}`,
          })),
        ];

        extra = {
          isMultiSplit: true,
          multiSplitRules: uiRules,
          defaultTo,
        };
      }
      else if (n.type === 'PERCENTAGE_SPLIT' || n.type === 'percent') {
        type = 'percent';
        label = 'Percentage Split';
        const buckets = n.cfg?.buckets || [{ name: 'A', weight: 50 }, { name: 'B', weight: 50 }];
        extra = { buckets: buckets.map((b: any, index: number) => ({ name: b.name || String.fromCharCode(65 + index), weight: b.weight || 50, to: b.to || '' })) };
      }
      else if (n.type === 'BRANCH_TRUE_FALSE' || n.type === 'branch') {
        type = 'branch';
        label = 'True/False Branch';

        const conditionData = n.cfg?.condition || { op: 'AND', conditions: [] };
        const branchConditions = (conditionData.conditions || []).map((cond: any) => ({
          entity: cond.entity || 'Contact',
          field: cond.field || '',
          operator: cond.operator || 'EXISTS',
          value: cond.value !== undefined ? cond.value : null,
        }));

        extra = {
          isBranch: true,
          branchConditions: branchConditions,
          conditionLogic: conditionData.op || 'AND'
        };
      }

      let rfType = 'custom';
      if (n.type === 'MULTI_SPLIT' || n.type === 'multi') rfType = 'multiSplit';
      else if (n.type === 'BRANCH_TRUE_FALSE' || n.type === 'branch') rfType = 'branch';

      flowNodes.push({
        id: n.id,
        type: rfType,
        data: {
          label,
          id: n.id,
          type,
          maxWaitAmount: extra.maxWaitAmount || 0,
          maxWaitUnit: extra.maxWaitUnit || '',
          amount: extra.amount || 0,
          unit: extra.unit || 'days',
          ...extra
        },
        position: { x: 0, y: 0 }
      });
    });

    Object.entries(apiNodes).forEach(([, n]: [string, any]) => {
      const sourceId = n.id;
      const source = n.type === 'TRIGGER' || n.id === 'nTRIGGER' ? 'home' : sourceId;
      if (n.edges?.next) flowEdges.push({ id: `e${source}-${n.edges.next}`, source, target: n.edges.next, style: edgeStyle, animated: true });
      if (n.edges?.true) flowEdges.push({ id: `e${sourceId}-${n.edges.true}`, source: sourceId, target: n.edges.true, sourceHandle: 'true', style: edgeStyle, animated: true });
      if (n.edges?.false) flowEdges.push({ id: `e${sourceId}-${n.edges.false}`, source: sourceId, target: n.edges.false, sourceHandle: 'false', style: edgeStyle, animated: true });
      if (n.type === 'PERCENTAGE_SPLIT' && n.cfg?.buckets) n.cfg.buckets.forEach((bucket: any, index: number) => { if (bucket.to) flowEdges.push({ id: `e${sourceId}-${bucket.to}`, source: sourceId, target: bucket.to, sourceHandle: `percent-${index}`, style: edgeStyle, animated: true }); });
      if ((n.type === 'MULTI_SPLIT' || n.type === 'multi')) {
        if (n.cfg?.defaultTo) {
          flowEdges.push({ id: `e${sourceId}-${n.cfg.defaultTo}`, source: sourceId, target: n.cfg.defaultTo, sourceHandle: 'rule-rule-default', style: edgeStyle, animated: true });
        }
        if (n.cfg?.rules) {
          n.cfg.rules.forEach((rule: any, index: number) => {
            if (rule.to) {
              const ruleId = rule.id || `rule-${index + 1}`;
              flowEdges.push({ id: `e${sourceId}-${rule.to}`, source: sourceId, target: rule.to, sourceHandle: `rule-${ruleId}`, style: edgeStyle, animated: true });
            }
          });
        }
      }
    });

    const percentNodes = flowNodes.filter((n) => n.data.type === 'percent');
    percentNodes.forEach((percentNode) => {
      const buckets = percentNode.data.buckets || [];
      buckets.forEach((bucket: any, index: number) => {
        const edge = flowEdges.find((e) => e.source === percentNode.id && e.sourceHandle === `percent-${index}`);
        if (edge) {
          let currentNodeId = edge.target;
          const visited = new Set<string>();
          while (currentNodeId && !visited.has(currentNodeId)) {
            visited.add(currentNodeId);
            const targetNode = flowNodes.find((n) => n.id === currentNodeId);
            if (targetNode && targetNode.data.isExit) { targetNode.data.label = `Exit ${bucket.name || String.fromCharCode(65 + index)}`; break; }
            const nextEdge = flowEdges.find((e) => e.source === currentNodeId && !e.sourceHandle);
            currentNodeId = nextEdge?.target || '';
          }
        }
      });
    });

    return { nodes: flowNodes, edges: flowEdges };
  }

  const cleanedNodes = initialNodes
    .filter((n) => n.id !== 'nTRIGGER' && n.type !== 'TRIGGER' && n.label?.toLowerCase() !== 'trigger')
    .map((node) => {
      let label = node.label;
      if (node.type === 'send') label = 'Send Email';
      if (node.type === 'exit' || node.id.toLowerCase().includes('exit')) label = 'Exit';
      return {
        id: node.id,
        type: node.id === 'home' ? 'customHome' : node.type === 'branch' ? 'branch' : node.isMultiSplit || node.type === 'multi' ? 'multiSplit' : 'custom',
        data: {
          label, id: node.id, type: node.type, tag: node.tag || '', selectedTags: node.selectedTags || [],
          template: node.template || '', variantId: node.variantId || node.template || '',
          variables: node.variables || { name: '', address: '' },
          delayType: node.delayType === 'fixed' || node.delayType === 'specific' ? node.delayType : undefined,
          amount: node.amount || 0,
          unit: node.unit === 'days' || node.unit === 'weeks' || node.unit === 'months' ? node.unit : undefined,
          date: node.date || '', time: node.time || '', conditions: node.conditions || [],
          maxWaitAmount: node.maxWaitAmount || 0, maxWaitUnit: node.maxWaitUnit || '',
          isExit: node.id === 'exit' || node.type === 'exit' || node.id.toLowerCase().includes('exit'),
          trigger: automation?.trigger || '', isMultiSplit: node.isMultiSplit || node.type === 'multi' || false,
          multiSplitRules: node.multiSplitRules || [{ id: uuidv4(), condition: '', to: '', label: 'Default' }],
          defaultTo: node.defaultTo || '',
          buckets: node.buckets || [{ name: 'A', weight: 50 }, { name: 'B', weight: 50 }],
          isBranch: node.isBranch || false,
          branchConditions: node.branchConditions || [],
          conditionLogic: node.conditionLogic || 'AND',
        },
        position: node.position || { x: 0, y: 0 },
      } as Node<NodeData>;
    });

  if (!cleanedNodes.some((n) => n.id === 'home')) {
    cleanedNodes.unshift({ id: 'home', type: 'customHome', data: { label: 'Trigger', id: 'home', trigger: automation?.trigger || '' }, position: { x: 0, y: 0 } });
  }

  const cleanedEdges = initialEdges
    .filter((e) => e.source !== 'nTRIGGER' && e.target !== 'nTRIGGER')
    .map((e) => {
      const source = e.source === 'nTRIGGER' || e.source.toLowerCase().includes('trigger') ? 'home' : e.source;
      return { id: e.id, source, target: e.target, sourceHandle: e.sourceHandle, style: edgeStyle, animated: true };
    });

  const originalTriggerEdge = initialEdges.find((e) => e.source === 'nTRIGGER');
  if (originalTriggerEdge && !cleanedEdges.some((e) => e.source === 'home')) {
    cleanedEdges.push({ id: `ehome-${originalTriggerEdge.target}`, source: 'home', target: originalTriggerEdge.target, style: edgeStyle, animated: true, sourceHandle: undefined });
  }

  return { nodes: cleanedNodes, edges: cleanedEdges };
}

// ── Main HierarchyGraphEdit Component ──
const HierarchyGraphEdit = forwardRef<{ nodes: Node<NodeData>[]; edges: Edge[] }, HierarchyGraphEditProps>(
  ({ initialNodes = [], initialEdges = [], automation }, ref) => {
    const exitNodeId = useRef('exit');
    const { nodes: normalizedNodes, edges: normalizedEdges } = useMemo(() => normalizeFromApi(initialNodes, initialEdges, automation), [initialNodes, initialEdges, automation]);
    const [nodes, setNodes, onNodesChange] = useNodesState<NodeData>(normalizedNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(normalizedEdges);
    const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(null);
    const [activeItem, setActiveItem] = useState<{ type: string; label: string } | null>(null);
    const isInitialLoad = useRef(true);
    const localRef = useRef({ nodes, edges });
    const nodesRef = useRef<Node<NodeData>[]>(nodes);
    const edgesRef = useRef<Edge[]>(edges);

    useEffect(() => { localRef.current = { nodes, edges }; nodesRef.current = nodes; edgesRef.current = edges; }, [nodes, edges]);

    const onNodeClick = useCallback((node: Node<NodeData>) => { if (node.data.isExit) return; setSelectedNode(node); }, []);

    const onDeleteConnection = useCallback((nodeId: string) => {
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    }, [setEdges]);

    const onDeleteNode = useCallback((nodeId: string) => {
      if (nodeId === 'home') return;
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const nodeToDelete = currentNodes.find((n) => n.id === nodeId);
      if (!nodeToDelete || nodeToDelete.data.isExit) return;
      const edgeStyle = { stroke: '#D000E5', strokeWidth: 2, filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))' };

      if (nodeToDelete.data.type === 'percent' || nodeToDelete.data.type === 'multi' || nodeToDelete.data.type === 'branch') {
        const nodesToDeleteIds = new Set<string>([nodeId]);
        const queue: string[] = [nodeId];
        while (queue.length > 0) { const curr = queue.shift()!; currentEdges.forEach((edge) => { if (edge.source === curr && !nodesToDeleteIds.has(edge.target)) { nodesToDeleteIds.add(edge.target); queue.push(edge.target); } }); }
        const incomingEdge = currentEdges.find((e) => e.target === nodeId);
        const upstreamSourceId = incomingEdge ? incomingEdge.source : 'home';
        const upstreamSourceHandle = incomingEdge ? incomingEdge.sourceHandle : undefined;
        const remainingNodes = currentNodes.filter((n) => !nodesToDeleteIds.has(n.id));
        const remainingEdges = currentEdges.filter((e) => !nodesToDeleteIds.has(e.source) && !nodesToDeleteIds.has(e.target));
        const existingExit = remainingNodes.find((n) => n.data.isExit);
        let updatedNodes = remainingNodes, updatedEdges = remainingEdges;
        if (!existingExit) {
          const newExitId = exitNodeId.current;
          const newExit: Node<NodeData> = { id: newExitId, type: 'custom', data: { label: 'Exit', type: 'exit', id: newExitId, isExit: true, onNodeClick, onDeleteConnection, onDeleteNode }, position: { x: 0, y: 0 } };
          updatedNodes = [...remainingNodes, newExit];
          updatedEdges = [...remainingEdges, { id: `e${upstreamSourceId}-${newExitId}`, source: upstreamSourceId, target: newExitId, sourceHandle: upstreamSourceHandle, style: edgeStyle, animated: true }];
        } else if (!remainingEdges.some((e) => e.target === existingExit.id)) {
          updatedEdges = [...remainingEdges, { id: `e${upstreamSourceId}-${existingExit.id}`, source: upstreamSourceId, target: existingExit.id, sourceHandle: upstreamSourceHandle, style: edgeStyle, animated: true }];
        }
        setSelectedNode((prev) => (prev && nodesToDeleteIds.has(prev.id) ? null : prev));
        const { nodes: ln, edges: le } = getLayoutedElements(updatedNodes, updatedEdges);
        setNodes(syncMultiSplitData(ln, le));
        setEdges(le);
        return;
      }

      const incomingEdge = currentEdges.find((e) => e.target === nodeId);
      const outgoingEdge = currentEdges.find((e) => e.source === nodeId);
      const remainingNodes = currentNodes.filter((n) => n.id !== nodeId);
      let remainingEdges = currentEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
      if (incomingEdge && outgoingEdge) {
        remainingEdges = [...remainingEdges, { id: `e${incomingEdge.source}-${outgoingEdge.target}`, source: incomingEdge.source, target: outgoingEdge.target, sourceHandle: incomingEdge.sourceHandle, style: edgeStyle, animated: true }];
      }
      setSelectedNode((prev) => (prev && prev.id === nodeId ? null : prev));
      const { nodes: ln, edges: le } = getLayoutedElements(remainingNodes, remainingEdges);
      setNodes(syncMultiSplitData(ln, le));
      setEdges(le);
    }, [setNodes, setEdges, onNodeClick, onDeleteConnection]);

    const onUpdateNode = useCallback((updatedNode: Node<NodeData>) => {
      const edgeStyle = { stroke: '#D000E5', strokeWidth: 2, filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))' };
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      if (updatedNode.data.isMultiSplit || updatedNode.data.type === 'multi') {
        const existingNode = currentNodes.find((n) => n.id === updatedNode.id);
        const prevRules: MultiSplitRule[] = existingNode?.data.multiSplitRules || [];
        const nextRules: MultiSplitRule[] = updatedNode.data.multiSplitRules || [];

        const prevRuleIds = new Set(prevRules.map((r) => r.id));
        const nextRuleIds = new Set(nextRules.map((r) => r.id));

        const addedRules = nextRules.filter((r) => !prevRuleIds.has(r.id));
        const removedRules = prevRules.filter((r) => !nextRuleIds.has(r.id));

        let updatedNodes = currentNodes.map((n) => (n.id === updatedNode.id ? { ...n, data: updatedNode.data } : n));
        let updatedEdges = [...currentEdges];

        if (removedRules.length > 0) {
          const removedHandles = new Set(removedRules.map((r) => `rule-${r.id}`));
          const directEdgesToRemove = updatedEdges.filter(
            (e) => e.source === updatedNode.id && e.sourceHandle && removedHandles.has(e.sourceHandle)
          );

          const nodesToDelete = new Set<string>();
          const queue: string[] = directEdgesToRemove.map((e) => e.target);

          while (queue.length > 0) {
            const curr = queue.shift()!;
            if (!nodesToDelete.has(curr)) {
              nodesToDelete.add(curr);
              updatedEdges.forEach((e) => {
                if (e.source === curr && !nodesToDelete.has(e.target)) {
                  queue.push(e.target);
                }
              });
            }
          }

          updatedEdges = updatedEdges.filter(
            (e) => !nodesToDelete.has(e.source) && !nodesToDelete.has(e.target) && !directEdgesToRemove.includes(e)
          );
          updatedNodes = updatedNodes.filter((n) => !nodesToDelete.has(n.id));
        }

        if (addedRules.length > 0) {
          const newExitNodes: Node<NodeData>[] = [];
          const newExitEdges: Edge[] = [];

          addedRules.forEach((rule) => {
            const exitId = uuidv4();
            newExitNodes.push({
              id: exitId,
              type: 'custom',
              data: {
                label: `Exit ${rule.label}`,
                type: 'exit',
                id: exitId,
                isExit: true,
                onNodeClick,
                onDeleteConnection,
                onDeleteNode,
              },
              position: { x: 0, y: 0 },
            });

            newExitEdges.push({
              id: `e${updatedNode.id}-${exitId}`,
              source: updatedNode.id,
              target: exitId,
              sourceHandle: `rule-${rule.id}`,
              style: edgeStyle,
              animated: true,
            });
          });

          updatedNodes = [...updatedNodes, ...newExitNodes];
          updatedEdges = [...updatedEdges, ...newExitEdges];
        }

        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(updatedNodes, updatedEdges);
        const syncedNodes = syncMultiSplitData(layoutedNodes, layoutedEdges);
        setNodes(syncedNodes);
        setEdges(layoutedEdges);
        setSelectedNode(syncedNodes.find((n) => n.id === updatedNode.id) || updatedNode);
        return;
      }

      setNodes((nds) => nds.map((n) => (n.id === updatedNode.id ? { ...n, ...updatedNode, position: updatedNode.position || n.position, data: { ...n.data, ...updatedNode.data } } : n)));
      setSelectedNode((prev) => (prev && prev.id === updatedNode.id ? { ...prev, data: { ...prev.data, ...updatedNode.data } } : prev));
    }, [setNodes, setEdges, onNodeClick, onDeleteConnection, onDeleteNode]);

    useEffect(() => {
      if (nodes.length === 0) return;
      setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, onNodeClick, onDeleteConnection, onDeleteNode, onUpdateNode } })));
    }, [onNodeClick, onDeleteConnection, onDeleteNode, onUpdateNode, setNodes]);

    useEffect(() => {
      if (isInitialLoad.current && normalizedNodes.length > 0) {
        const { nodes: layouted } = getLayoutedElements(normalizedNodes, normalizedEdges);
        setNodes(syncMultiSplitData(layouted, normalizedEdges).map((n) => ({ ...n, data: { ...n.data, onNodeClick, onDeleteConnection, onDeleteNode, onUpdateNode } })));
        setEdges(normalizedEdges);
        isInitialLoad.current = false;
      }
    }, [normalizedNodes, normalizedEdges, setNodes, setEdges, onNodeClick, onDeleteConnection, onDeleteNode, onUpdateNode]);

    useImperativeHandle(ref, () => ({
      get nodes() {
        return syncMultiSplitData(localRef.current.nodes, localRef.current.edges);
      },
      get edges() {
        return localRef.current.edges;
      },
    }));

    const onConnect = useCallback((params: Connection) => {
      if (params.target === 'home') return;
      if (params.source === exitNodeId.current) return;
      setEdges((eds) => {
        let filtered = eds;
        const existingEdge = eds.find((e) => e.target === params.target);
        if (existingEdge) filtered = eds.filter((e) => e.id !== existingEdge.id);
        const nextEdges = addEdge({ id: `e${params.source!}-${params.target!}`, source: params.source!, target: params.target!, sourceHandle: params.sourceHandle || undefined, style: { stroke: '#D000E5', strokeWidth: 2, filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))' }, animated: true }, filtered);
        setNodes((nds) => syncMultiSplitData(nds, nextEdges));
        return nextEdges;
      });
    }, [setEdges, setNodes]);

    const handleDragStart = useCallback((event: DragStartEvent) => { setActiveItem(event.active.data.current as { type: string; label: string }); }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      setActiveItem(null);
      if (!event.over) return;

      const { label, type } = event.active.data.current as { label: string; type: string };
      if (type === 'exit') return;

      const conditionNodeTypes = ['multi', 'percent', 'branch'];
      if (conditionNodeTypes.includes(type)) {
        const hasExistingCondition = nodes.some((node) => {
          return (
            node.data.type === 'multi' ||
            node.data.isMultiSplit ||
            node.data.type === 'percent' ||
            node.data.isBranch
          );
        });

        if (hasExistingCondition) {
          toast.error(`Cannot add "${label}". Only one condition node (Multi Split, Percentage Split, or True/False Branch) is allowed per automation.`, {
            position: 'top-right',
            autoClose: 4000,
            theme: 'colored',
            style: { backgroundColor: '#fef2f2', color: '#dc2626' },
          });
          return;
        }
      }

      const hasConditionNode = nodes.some((node) => {
        return (
          node.data.type === 'multi' ||
          node.data.isMultiSplit ||
          node.data.type === 'percent' ||
          node.data.isBranch
        );
      });

      if (hasConditionNode) {
        const overData = event.over.data.current as any;
        const isBranchTarget = overData?.type === 'branch-target' || overData?.type === 'multi-branch-target' || overData?.type === 'truefalse-branch-target';

        if (!isBranchTarget) {
          toast.warning('When a condition node exists, you can only drop nodes on the branch handles (A/B for Percentage Split, Default/Condition for Multi Split, or True/False for Branch).', {
            position: 'top-right',
            autoClose: 4000,
            theme: 'colored',
            style: { backgroundColor: '#fef3c7', color: '#d97706' },
          });
          return;
        }
      }

      const newNodeId = uuidv4();
      const isBranch = type === 'branch';
      const isMultiSplit = type === 'multi';
      const isPercent = type === 'percent';
      const nodeType = isBranch ? 'branch' : isMultiSplit ? 'multiSplit' : 'custom';

      const newNode: Node<NodeData> = {
        id: newNodeId,
        type: nodeType,
        data: {
          label,
          type,
          id: newNodeId,
          tag: type === 'tags' ? '' : undefined,
          selectedTags: type === 'tags' ? [] : undefined,
          template: type === 'send' ? '' : undefined,
          variantId: type === 'send' ? '' : undefined,
          variables: type === 'send' ? { name: '', address: '' } : undefined,
          conditions: type === 'wait' ? [] : undefined,
          maxWaitAmount: type === 'wait' ? 0 : undefined,
          maxWaitUnit: type === 'wait' ? '' : undefined,
          buckets: isPercent ? [{ name: 'A', weight: 50, to: '' }, { name: 'B', weight: 50, to: '' }] : undefined,
          isBranch,
          branchConditions: isBranch ? [] : undefined,
          conditionLogic: isBranch ? 'AND' : undefined,
          isMultiSplit,
          multiSplitRules: isMultiSplit ? [{ id: uuidv4(), condition: '', to: '', label: 'Default' }] : undefined,
          defaultTo: isMultiSplit ? '' : undefined,
          onNodeClick,
          onDeleteConnection,
          onDeleteNode,
          onUpdateNode,
        },
        position: { x: 0, y: 0 },
      };

      const overData = event.over.data.current as any;
      let targetSourceNodeId: string | null = null;
      let targetSourceHandle: string | undefined = undefined;
      let targetDownstreamExitId: string | null = null;

      if (overData?.type === 'branch-target') {
        targetSourceNodeId = overData.nodeId;
        targetSourceHandle = `percent-${overData.branchIndex}`;
        const existingBranchEdge = edges.find((e) => e.source === targetSourceNodeId && e.sourceHandle === targetSourceHandle);
        if (existingBranchEdge) targetDownstreamExitId = existingBranchEdge.target;
      } else if (overData?.type === 'multi-branch-target') {
        targetSourceNodeId = overData.nodeId;
        targetSourceHandle = `rule-${overData.ruleId}`;
        const existingBranchEdge = edges.find((e) => e.source === targetSourceNodeId && e.sourceHandle === targetSourceHandle);
        if (existingBranchEdge) targetDownstreamExitId = existingBranchEdge.target;
      } else if (overData?.type === 'truefalse-branch-target') {
        targetSourceNodeId = overData.nodeId;
        targetSourceHandle = overData.handleType;
        const existingBranchEdge = edges.find((e) => e.source === targetSourceNodeId && e.sourceHandle === targetSourceHandle);
        if (existingBranchEdge) targetDownstreamExitId = existingBranchEdge.target;
      } else if (overData?.type === 'exit-target') {
        const incomingEdge = edges.find((e) => e.target === overData.exitNodeId);
        if (incomingEdge) {
          targetSourceNodeId = incomingEdge.source;
          targetSourceHandle = incomingEdge.sourceHandle || undefined;
          targetDownstreamExitId = overData.exitNodeId;
        }
      }

      if (!targetSourceNodeId) {
        const nonExitNodes = nodes.filter((n) => !n.data.isExit && n.id !== 'home');
        const lastNode = nonExitNodes.length > 0 ? nonExitNodes[nonExitNodes.length - 1] : nodes.find((n) => n.id === 'home');
        const exitNode = nodes.find((n) => n.data.isExit);
        if (lastNode && exitNode) {
          targetSourceNodeId = lastNode.id;
          targetDownstreamExitId = exitNode.id;
        }
      }

      if (!targetSourceNodeId) return;

      const edgeStyle = { stroke: '#D000E5', strokeWidth: 2, filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))' };

      if (isBranch || isPercent || isMultiSplit) {
        let branchLabels: string[] = [];
        let branchHandles: string[] = [];

        if (isBranch) {
          branchLabels = ['True', 'False'];
          branchHandles = ['true', 'false'];
        } else if (isPercent) {
          branchLabels = newNode.data.buckets?.map((b) => b.name) || ['A', 'B'];
          branchHandles = branchLabels.map((_, index) => `percent-${index}`);
        } else if (isMultiSplit) {
          branchLabels = newNode.data.multiSplitRules?.map((r) => r.label) || ['Default'];
          branchHandles = newNode.data.multiSplitRules?.map((r) => `rule-${r.id}`) || ['rule-default'];
        }

        const newExitNodes: Node<NodeData>[] = [];
        const newExitEdges: Edge[] = [];

        branchLabels.forEach((bLabel, index) => {
          const exitId = uuidv4();
          newExitNodes.push({
            id: exitId,
            type: 'custom',
            data: {
              label: `Exit ${bLabel}`,
              type: 'exit',
              id: exitId,
              isExit: true,
              onNodeClick,
              onDeleteConnection,
              onDeleteNode,
            },
            position: { x: 0, y: 0 },
          });

          newExitEdges.push({
            id: `e${newNode.id}-${exitId}`,
            source: newNode.id,
            target: exitId,
            sourceHandle: branchHandles[index],
            style: edgeStyle,
            animated: true,
          });
        });

        const edgeToNewNode: Edge = {
          id: `e${targetSourceNodeId}-${newNode.id}`,
          source: targetSourceNodeId,
          target: newNode.id,
          sourceHandle: targetSourceHandle,
          style: edgeStyle,
          animated: true,
        };

        const filteredNodes = targetDownstreamExitId ? nodes.filter((n) => n.id !== targetDownstreamExitId) : nodes;
        const filteredEdges = edges.filter((e) => {
          if (targetDownstreamExitId && e.target === targetDownstreamExitId) return false;
          if (targetSourceHandle && e.source === targetSourceNodeId && e.sourceHandle === targetSourceHandle) return false;
          return true;
        });

        const { nodes: ln, edges: le } = getLayoutedElements(
          [...filteredNodes, newNode, ...newExitNodes],
          [...filteredEdges, edgeToNewNode, ...newExitEdges]
        );

        setNodes(syncMultiSplitData(ln, le));
        setEdges(le);
      } else {
        const newEdgeToNewNode: Edge = {
          id: `e${targetSourceNodeId}-${newNode.id}`,
          source: targetSourceNodeId,
          target: newNode.id,
          sourceHandle: targetSourceHandle,
          style: edgeStyle,
          animated: true,
        };

        const newEdgeToExit: Edge | null = targetDownstreamExitId
          ? {
            id: `e${newNode.id}-${targetDownstreamExitId}`,
            source: newNode.id,
            target: targetDownstreamExitId,
            style: edgeStyle,
            animated: true,
          }
          : null;

        const filteredEdges = edges.filter((edge) => {
          if (targetDownstreamExitId && edge.source === targetSourceNodeId && edge.target === targetDownstreamExitId) return false;
          if (targetSourceHandle && edge.source === targetSourceNodeId && edge.sourceHandle === targetSourceHandle) return false;
          return true;
        });

        const allNewEdges = newEdgeToExit
          ? [...filteredEdges, newEdgeToNewNode, newEdgeToExit]
          : [...filteredEdges, newEdgeToNewNode];

        const { nodes: ln, edges: le } = getLayoutedElements([...nodes, newNode], allNewEdges);

        setNodes(syncMultiSplitData(ln, le));
        setEdges(le);
      }
    }, [nodes, edges, setNodes, setEdges, onNodeClick, onDeleteConnection, onDeleteNode, onUpdateNode]);

    const handleDragCancel = useCallback(() => setActiveItem(null), []);
    const onPaneClick = useCallback(() => setSelectedNode(null), []);

    const renderDetailPanel = useCallback(() => {
      if (!selectedNode) return <div className="h-full flex items-center justify-center text-gray-400 text-sm">Select a node to configure</div>;
      if (selectedNode.data.type === 'percent') return <PercentageSplitDetailPanel node={selectedNode} onUpdateNode={onUpdateNode} />;
      if (selectedNode.data.type === 'multi') return <MultiSplitDetailPanel node={selectedNode} onUpdateNode={onUpdateNode} />;
      if (selectedNode.data.type === 'branch' || selectedNode.data.isBranch) return <BranchDetailPanel node={selectedNode} onUpdateNode={onUpdateNode} />;
      return <DetailPanel node={selectedNode as any} onUpdateNode={onUpdateNode as any} />;
    }, [selectedNode, onUpdateNode]);

    return (
      <DndContext collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="flex gap-4 px-4 pb-4">
          <div className="w-60 bg-white border rounded-lg p-4 flex-shrink-0 shadow-md">
            <div className="font-semibold mb-2 text-[#3F5265] text-[16px] pb-4 border-b border-gray-300">Flow Elements</div>
            <div className="text-xs text-gray-500 mb-2 pt-2">Action</div>
            <SidebarItem type="send" label="Send Email" />
            <SidebarItem type="tags" label="Tags" />
            <div className="text-xs text-gray-500 my-2 pt-2">Delay</div>
            <SidebarItem type="wait" label="Wait Until" />
            <div className="text-xs text-gray-500 my-2 pt-2">Flow Condition</div>
            <SidebarItem type="branch" label="True/False Branch" />
            <SidebarItem type="multi" label="Multi Split" />
            <SidebarItem type="percent" label="Percentage Split" />
          </div>
          <DroppableCanvas id="reactflow-canvas">
            <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeClick={(_, node) => onNodeClick(node)} onPaneClick={onPaneClick} nodeTypes={nodeTypes} defaultViewport={{ x: 220, y: 50, zoom: 0.9 }} nodesDraggable nodesConnectable elementsSelectable>
              <Controls /><Background color="#aaa" gap={16} />
            </ReactFlow>
          </DroppableCanvas>
          <div className="w-80 h-[650px] bg-white border rounded-lg p-4 flex-shrink-0 shadow-md overflow-y-auto">{renderDetailPanel()}</div>
          <DragOverlay>{activeItem ? <div className="p-2 bg-white rounded border shadow-lg cursor-grabbing text-sm">{activeItem.label}</div> : null}</DragOverlay>
        </div>
      </DndContext>
    );
  }
);

export default HierarchyGraphEdit;